import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { toast } from 'react-toastify';
import { useIndustries } from 'services/industryService';
import {
    AccountPrice,
    authConfig,
    fetchAccountPrices,
    fetchOverview,
    fetchPurchaseOrderVolumes,
    marketFor,
    plural,
    PurchaseOrderVolume,
    sheetTitle,
    TemplateOverview,
} from 'services/integrations';
import { IntegrationsTabs } from './pricing';

/**
 * "Mes prix" (`/integrations/prices`): the account's minimum price per activity. These prices are
 * proposed by default whenever an integration is created or a page's activity chosen, and always
 * used by the API. An integration may set its own price for an activity, which then wins for it —
 * listed here under "Utilisé par" so the seller sees every place a price applies.
 */

interface Row {
    key: string;
    industry: string;
    subIndustry: string;
    account?: AccountPrice;
    usedBy: { label: string; own?: number | null }[];
    unpricedUse: boolean;
}

const IntegrationPrices = () => {
    const navigate = useNavigate();
    const { industries } = useIndustries();
    const [prices, setPrices] = useState<AccountPrice[]>([]);
    const [overview, setOverview] = useState<TemplateOverview[]>([]);
    const [volumes, setVolumes] = useState<PurchaseOrderVolume[]>([]);
    const [user, setUser] = useState<any>(null);
    const [hasApiKey, setHasApiKey] = useState(false);
    const [drafts, setDrafts] = useState<Record<string, string>>({});
    const [saving, setSaving] = useState<string | null>(null);
    const [adding, setAdding] = useState({ industry: '', subIndustry: '', price: '' });

    const reload = () =>
        Promise.all([fetchAccountPrices().then(setPrices), fetchOverview().then(setOverview)]).catch(() =>
            toast.error('Erreur lors du chargement de vos prix')
        );

    useEffect(() => {
        reload();
        fetchPurchaseOrderVolumes()
            .then(setVolumes)
            .catch(() => {});
        axios
            .get(`${CONFIG.apiUrl}/user/me`, authConfig())
            .then((res) => setUser(res.data))
            .catch(() => {});
        axios
            .get(`${CONFIG.apiUrl}/account/api-key`, authConfig())
            .then((res) => setHasApiKey(!!res.data?.apiKey))
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // One row per activity with an account price or used by an integration.
    const rows = useMemo(() => {
        const map = new Map<string, Row>();
        const row = (industry: string, subIndustry: string) => {
            const key = `${industry}|${subIndustry}`;
            if (!map.has(key)) map.set(key, { key, industry, subIndustry, usedBy: [], unpricedUse: false });
            return map.get(key)!;
        };
        prices.forEach((p) => (row(p.industry, p.subIndustry || '').account = p));
        overview.forEach((t) => {
            if (t.isUniversal) {
                const where = t.source === 'mcp' ? (t.sites[0]?.hostname || t.name) : 'Formulaire';
                t.activities.forEach((a) => {
                    const r = row(a.industry, a.subIndustry);
                    r.usedBy.push({ label: a.pages ? `${where} · ${plural(a.pages, 'page', 'pages')}` : where, own: a.integrationPrice });
                    if (a.minimumPrice == null) r.unpricedUse = true;
                });
            } else if (t.industry) {
                const r = row(t.industry, t.subIndustry || '');
                r.usedBy.push({ label: t.sheetSyncEnabled ? sheetTitle(t) : t.name, own: t.minimumPrice });
            }
        });
        return Array.from(map.values()).sort((a, b) => Number(b.unpricedUse) - Number(a.unpricedUse) || a.industry.localeCompare(b.industry));
    }, [prices, overview]);

    const unpriced = rows.filter((r) => !r.account && r.unpricedUse).length;

    const save = async (industry: string, subIndustry: string, raw: string) => {
        const key = `${industry}|${subIndustry}`;
        const value = parseFloat((raw || '').replace(',', '.'));
        if (!raw || isNaN(value) || value <= 0) {
            toast.error('Indiquez un prix minimum valide');
            return false;
        }
        setSaving(key);
        try {
            await axios.put(`${CONFIG.apiUrl}/seller-industry-price`, { industry, subIndustry: subIndustry || undefined, minimumPrice: value }, authConfig());
            toast.success('Prix du compte enregistré');
            setDrafts((d) => {
                const next = { ...d };
                delete next[key];
                return next;
            });
            await reload();
            return true;
        } catch (e: any) {
            toast.error(e?.response?.data?.message || "Erreur lors de l'enregistrement du prix");
            return false;
        } finally {
            setSaving(null);
        }
    };

    const remove = async (price: AccountPrice) => {
        if (!price._id) return;
        if (!window.confirm("Supprimer ce prix du compte ? Les leads envoyés par API pour cette activité seront refusés tant qu'il n'y a pas de prix.")) return;
        try {
            await axios.delete(`${CONFIG.apiUrl}/seller-industry-price/${price._id}`, authConfig());
            toast.success('Prix supprimé');
            reload();
        } catch {
            toast.error('Erreur lors de la suppression');
        }
    };

    const subs = industries.find((i) => i.name === adding.industry)?.subIndustries || [];
    const select = 'rounded-[9px] border border-[#DDD5C9] bg-white px-2.5 py-2 font-hanken text-[13.5px] outline-none focus:border-[#241C5C]';

    return (
        <div className="mx-auto flex max-w-[1140px] flex-col gap-5 pb-16 pt-2 font-hanken text-[#1A1613]">
            <IntegrationsTabs active="prices" unpriced={unpriced} />

            <div>
                <h1 className="font-newsreader text-[34px] font-medium leading-[1.05] tracking-[-0.02em] text-[#14110E]">Mes prix</h1>
                <p className="mt-2 max-w-[720px] text-[14.5px] leading-relaxed text-[#6B6357]">
                    Votre prix minimum par lead, pour chaque activité. Il est{' '}
                    <strong className="text-[#3B352C]">proposé par défaut</strong> quand vous créez une intégration ou choisissez l'activité
                    d'une page, et <strong className="text-[#3B352C]">toujours utilisé pour l'envoi par API</strong>. Une intégration peut
                    fixer son propre prix : il prend alors le dessus pour elle.
                </p>
            </div>

            <div className="overflow-hidden rounded-[18px] border border-[#EDE7DE] bg-white">
                <div className="hidden grid-cols-[1.3fr_1fr_1.1fr_1.6fr] gap-3 border-b border-[#EDE7DE] bg-[#FAF8F4] px-5 py-2.5 text-[11.5px] font-bold uppercase tracking-[0.06em] text-[#A79E90] md:grid">
                    <span>Activité</span>
                    <span>Prix du compte</span>
                    <span>Marché</span>
                    <span>Utilisé par</span>
                </div>
                {rows.length === 0 && (
                    <p className="px-5 py-6 text-center text-[14px] text-[#8A8172]">Aucun prix pour l'instant. Ajoutez votre première activité ci-dessous.</p>
                )}
                {rows.map((r) => {
                    const market = marketFor(volumes, r.subIndustry, user);
                    const draft = drafts[r.key] ?? (r.account ? String(r.account.minimumPrice) : '');
                    const changed = draft !== (r.account ? String(r.account.minimumPrice) : '');
                    return (
                        <div
                            key={r.key}
                            className={`grid grid-cols-1 items-center gap-2.5 border-b border-[#F1ECE4] px-5 py-3.5 last:border-0 md:grid-cols-[1.3fr_1fr_1.1fr_1.6fr] md:gap-3 ${
                                !r.account && r.unpricedUse ? 'bg-[#FCF9EF]' : ''
                            }`}
                        >
                            <span>
                                <strong className="block text-[14.5px]">{r.subIndustry || r.industry}</strong>
                                {r.subIndustry && <span className="text-[12px] text-[#8A8172]">{r.industry}</span>}
                            </span>
                            <span className="flex flex-wrap items-center gap-2">
                                <span className="flex w-[96px] items-center rounded-[9px] border border-[#DDD5C9] bg-white">
                                    <input
                                        value={draft}
                                        placeholder="—"
                                        inputMode="decimal"
                                        aria-label={`Prix du compte, ${r.subIndustry || r.industry}`}
                                        onChange={(e) => setDrafts((d) => ({ ...d, [r.key]: e.target.value }))}
                                        onKeyDown={(e) => e.key === 'Enter' && save(r.industry, r.subIndustry, draft)}
                                        className="w-full min-w-0 bg-transparent px-2.5 py-1.5 text-[14px] font-semibold outline-none"
                                    />
                                    <span className="pr-2.5 text-[#9A9184]">€</span>
                                </span>
                                {changed && (
                                    <button
                                        onClick={() => save(r.industry, r.subIndustry, draft)}
                                        disabled={saving === r.key}
                                        className="rounded-[8px] bg-[#241C5C] px-2.5 py-1.5 text-[12.5px] font-semibold text-white hover:bg-[#332a75] disabled:opacity-50"
                                    >
                                        Enregistrer
                                    </button>
                                )}
                                {!r.account && r.unpricedUse && <span className="text-[12px] font-semibold text-[#7A5A05]">Pas de prix</span>}
                                {r.account && !changed && (
                                    <button onClick={() => remove(r.account!)} className="text-[12px] text-[#A79E90] hover:text-[#A8332B]" title="Supprimer ce prix">
                                        Supprimer
                                    </button>
                                )}
                            </span>
                            <span className="text-[12.5px] text-[#6B6357]">
                                {market.orders > 0
                                    ? `${plural(market.orders, 'ordre actif', 'ordres actifs')}${market.sellerPrice != null ? ` · dès ${market.sellerPrice} €` : ''}`
                                    : 'Pas de demande en ce moment'}
                            </span>
                            <span className="flex flex-wrap gap-1.5">
                                {r.usedBy.map((u) => (
                                    <span key={u.label} className="rounded-full bg-[#F5F3FF] px-2.5 py-[3px] text-[12px] text-[#312B78]">
                                        {u.label}
                                        {u.own != null && (!r.account || u.own !== r.account.minimumPrice) ? ` · prix propre ${u.own} €` : ''}
                                    </span>
                                ))}
                                {hasApiKey && <span className="rounded-full bg-[#E6F3FF] px-2.5 py-[3px] text-[12px] text-[#0E375C]">Envoi par API</span>}
                                {r.usedBy.length === 0 && !hasApiKey && <span className="text-[12px] text-[#A79E90]">Proposé par défaut</span>}
                            </span>
                        </div>
                    );
                })}
            </div>

            <div className="flex flex-wrap items-end gap-2.5 rounded-[16px] border border-dashed border-[#DDD5C9] bg-[#FAF8F4] p-4">
                <div className="flex flex-col gap-1">
                    <span className="text-[12.5px] font-semibold text-[#3B352C]">Ajouter une activité</span>
                    <span className="flex flex-wrap gap-2">
                        <select
                            value={adding.industry}
                            onChange={(e) => {
                                const sel = industries.find((i) => i.name === e.target.value);
                                setAdding((a) => ({ ...a, industry: e.target.value, subIndustry: sel?.subIndustries?.[0]?.name || '' }));
                            }}
                            className={select}
                            aria-label="Activité"
                        >
                            <option value="">Choisir une activité</option>
                            {industries.map((i) => (
                                <option key={i._id} value={i.name}>
                                    {i.name}
                                </option>
                            ))}
                        </select>
                        {subs.length > 0 && (
                            <select value={adding.subIndustry} onChange={(e) => setAdding((a) => ({ ...a, subIndustry: e.target.value }))} className={select} aria-label="Précision">
                                {subs.map((s: any) => (
                                    <option key={s.name} value={s.name}>
                                        {s.name}
                                    </option>
                                ))}
                            </select>
                        )}
                        <span className="flex w-[96px] items-center rounded-[9px] border border-[#DDD5C9] bg-white">
                            <input
                                value={adding.price}
                                onChange={(e) => setAdding((a) => ({ ...a, price: e.target.value }))}
                                placeholder="Prix"
                                inputMode="decimal"
                                aria-label="Prix minimum"
                                className="w-full min-w-0 bg-transparent px-2.5 py-2 text-[13.5px] font-semibold outline-none"
                            />
                            <span className="pr-2.5 text-[#9A9184]">€</span>
                        </span>
                    </span>
                </div>
                <button
                    onClick={async () => {
                        if (!adding.industry) {
                            toast.error('Choisissez une activité');
                            return;
                        }
                        if (await save(adding.industry, adding.subIndustry, adding.price)) setAdding({ industry: '', subIndustry: '', price: '' });
                    }}
                    className="rounded-[9px] bg-[#241C5C] px-3.5 py-2 text-[13px] font-semibold text-white hover:bg-[#332a75]"
                >
                    Ajouter
                </button>
                <button onClick={() => navigate('/integrations')} className="ml-auto text-[13px] font-semibold text-[#241C5C] hover:underline">
                    Retour à mes intégrations
                </button>
            </div>
        </div>
    );
};

export default IntegrationPrices;
